/// <reference types="@angular/localize" />

import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationRef, LOCALE_ID, Type, importProvidersFrom } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { createApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { devTools } from '@ngneat/elf-devtools';

import { environment } from './environments/environment';
import { Feature } from './app/modules/features/feature.enum';
import { TranslocoMatPaginatorIntl } from './app/paginator-intl';
import { TranslocoRootModule } from './app/transloco-root.module';
import { AuthTokenInterceptor } from './app/modules/shared/sv-common/interceptors/auth-token.interceptor';
import { JsonDateInterceptor } from './app/modules/shared/sv-common/interceptors/json-date.interceptor';
import { RoleService } from './app/modules/shared/sv-common/services/role-service.service';

// Webpack magic variable for lazy-loaded chunk base URL (used by dynamic imports).
/* eslint-disable @typescript-eslint/no-unused-vars, prefer-const */
declare let __webpack_public_path__: string;
__webpack_public_path__ = environment.webpackPath;
/* eslint-enable @typescript-eslint/no-unused-vars, prefer-const */

/**
 * Custom-Element-Registrierung für die SV-Angular-Feature-Komponenten.
 *
 * Historisch wurde ein `AppModule` mit `platformBrowserDynamic().bootstrapModule()`
 * verwendet und pro Feature ein `@NgModule`-Adapter mit `getComponent()`. Angular 20
 * unterstützt standalone-Bootstrap direkt via `createApplication`; die Komponenten
 * sind bereits `standalone: true`, daher entfallen die Wrapper-Module.
 *
 * Reihenfolge und `await`-Kette entsprechen dem alten `ngDoBootstrap` — bewusst
 * sequenziell, damit `configuration-loader` (Sprache/Rollen) vor abhängigen
 * Features registriert wird. Eine Parallelisierung ist ein separater Follow-up.
 */
async function bootstrap(): Promise<void> {
  registerLocaleData(localeDe, 'de-DE', localeDeExtra);

  if (!environment.production) {
    devTools();
    console.warn(
      'ACHTUNG: console.log ist nur im DEV-MODUS aktiviert. Auf PROD und STAGE muss es händisch eingeschaltet werden! Siehe Kommentar: main.ts',
    );
    console.warn('X_X_X_X_X_X_X_X_X_X_X_X_X');
  } else {
    if (window) {
      (window as { [key: string]: any })['log'] = console.log;
      (window as { [key: string]: any })['error'] = console.error;
      (window as { [key: string]: any })['debug'] = console.debug;
    }
    console.log = console.debug = console.error = () => {};
    // to temporary enable log output => console.log = log
  }

  const app = await createApplication({
    providers: [
      RoleService,
      { provide: HTTP_INTERCEPTORS, useClass: AuthTokenInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: JsonDateInterceptor, multi: true },
      { provide: MatPaginatorIntl, useClass: TranslocoMatPaginatorIntl },
      { provide: LOCALE_ID, useValue: 'de-DE' },
      provideHttpClient(withInterceptorsFromDi()),
      provideAnimations(),
      importProvidersFrom(TranslocoRootModule, NgbModule),
    ],
  });

  // Reihenfolge exakt wie im vorherigen ngDoBootstrap.
  await registerElement(app, Feature.DECKSCHEIN, () =>
    import('./app/modules/features/deckschein/deckschein.component').then((m) => m.DeckscheinComponent),
  );
  await registerElement(app, Feature.ZWINGER_DECKAKT_WELPEN_FORMULAR, () =>
    import('./app/modules/features/zwinger-deckakt/zwinger-deckakt.component').then((m) => m.ZwingerDeckaktComponent),
  );
  await registerElement(app, Feature.ZWINGER_DECKAKT_DETAILS, () =>
    import('./app/modules/features/zwinger-deckakt-details/zwinger-deckakt-details.component').then(
      (m) => m.ZwingerDeckaktDetailsComponent,
    ),
  );
  await registerElement(app, Feature.MELDUNG_FORMULAR, () =>
    import('./app/modules/features/meldeportal/meldung-formular/meldung-formular.component').then(
      (m) => m.MeldungFormularComponent,
    ),
  );
  await registerElement(app, Feature.MELDEPORTAL_TEILNEHMER_READONLY, () =>
    import(
      './app/modules/features/meldeportal/meldeportal-teilnehmer-readonly/meldeportal-teilnehmer-readonly.component'
    ).then((m) => m.MeldeportalTeilnehmerReadonlyComponent),
  );
  await registerElement(app, Feature.MELDEPORTAL_TEILNEHMER_LIST, () =>
    import('./app/modules/features/meldeportal/meldeportal-teilnehmer-list/meldeportal-teilnehmer-list.component').then(
      (m) => m.MeldeportalTeilnehmerListComponent,
    ),
  );
  await registerElement(app, Feature.MELDEPORTAL_TEILNEHMER_EDIT, () =>
    import('./app/modules/features/meldeportal/meldeportal-teilnehmer-edit/meldeportal-teilnehmer-edit.component').then(
      (m) => m.MeldeportalTeilnehmerEditComponent,
    ),
  );
  await registerElement(app, Feature.MELDEPORTAL_TEILNEHMER_STATS, () =>
    import('./app/modules/features/meldeportal-teilnehmer-stats/meldeportal-teilnehmer-stats.component').then(
      (m) => m.MeldeportalTeilnehmerStatsComponent,
    ),
  );
  await registerElement(app, Feature.MELDEPORTAL_MEINE_MELDUNGEN_LIST, () =>
    import('./app/modules/features/meldeportal/meine-meldungen-list/meine-meldungen-list.component').then(
      (m) => m.MeineMeldungenListComponent,
    ),
  );
  await registerElement(app, Feature.NOTIFICATION_BOX, () =>
    import('./app/modules/features/notification/notification.component').then((m) => m.NotificationComponent),
  );
  await registerElement(app, Feature.MELDEPORTAL_GEMELDETE_TEILNEHMER_LIST, () =>
    import('./app/modules/features/meldeportal/gemeldete-teilnehmer-list/gemeldete-teilnehmer-list.component').then(
      (m) => m.GemeldeteTeilnehmerListComponent,
    ),
  );
  await registerElement(app, Feature.USER_ADDRESS_UPDATE_REMINDER, () =>
    import('./app/modules/features/user-adress-reminder/user-adress-reminder.component').then(
      (m) => m.UserAdressReminderComponent,
    ),
  );
  await registerElement(app, Feature.CONFIGURATION_LOADER, () =>
    import('./app/modules/features/configuration-loader/configuration-loader.component').then(
      (m) => m.ConfigurationLoaderComponent,
    ),
  );
}

async function registerElement(
  app: ApplicationRef,
  elementName: string,
  loadComponent: () => Promise<Type<unknown>>,
): Promise<void> {
  if (customElements.get(elementName)) {
    console.warn(`Custom element '${elementName}' is already defined.`);
    return;
  }
  const component = await loadComponent();
  const customElement = createCustomElement(component, { injector: app.injector });
  customElements.define(elementName, customElement);
}

bootstrap().catch((err) => console.error(err));
